import React, { useState } from 'react';
import TextInput from 'react/elements/TextInput';

/**
 * Words worth offering for what has been typed: `candidates` not in `exclude`, and when something is
 * typed, those containing it, the ones starting with it first. Compared ignoring case; at most
 * `count`.
 */
export function suggest(candidates, exclude, typed, count) {
	const has = new Set(exclude.map((word) => word.toLowerCase()));
	const needle = typed.trim().toLowerCase();
	const offered = candidates.filter((word) => !has.has(word.toLowerCase()) && word.toLowerCase().includes(needle));
	const starts = offered.filter((word) => word.toLowerCase().startsWith(needle));
	const rest = offered.filter((word) => !word.toLowerCase().startsWith(needle));
	return [...starts, ...rest].slice(0, count);
}

/**
 * A text box with suggestions under it, for the browser's own autocomplete's job where that cannot
 * be given the list: tags in the item editor, ingredient names in a meal idea.
 *
 * The caller works out `suggestions` from `value` (see suggest()) and shows nothing by passing none.
 * The menu opens while the box has focus. The arrow keys move through it, down stopping at the last
 * and up past the first going back to what was typed; Escape closes it. A key in `submitKeys` calls
 * `onSubmit` with the highlighted suggestion, or with what was typed when none is, and so does a
 * suggestion clicked. `menuId` names the menu for the combobox's aria attributes. Anything else is
 * passed to the input; `onFocus` and `onBlur` are called as well as what the menu does on them.
 */
export default function SuggestingInput({
	value,
	onChange,
	suggestions,
	onSubmit,
	menuId,
	submitKeys = ['Enter'],
	menuClassName = 'suggesting_input__menu',
	onFocus = () => {},
	onBlur = () => {},
	...inputProps
}) {
	const [open, setOpen] = useState(false);
	// -1 for what was typed, otherwise the suggestion the arrow keys or the mouse are on.
	const [highlighted, setHighlighted] = useState(-1);
	// A read-only box takes nothing, typed or picked.
	const showMenu = open && suggestions.length > 0 && !inputProps.readOnly;

	const submit = (text) => {
		setHighlighted(-1);
		onSubmit(text);
	};

	const onKeyDown = (event) => {
		if(event.key === 'ArrowDown' || event.key === 'ArrowUp') {
			event.preventDefault();
			setOpen(true);
			setHighlighted((current) => (event.key === 'ArrowDown'
				? Math.min(current + 1, suggestions.length - 1)
				: Math.max(current - 1, -1)));
			return;
		}
		if(event.key === 'Escape' && showMenu) {
			event.preventDefault();
			event.stopPropagation();
			setOpen(false);
			setHighlighted(-1);
			return;
		}
		if(submitKeys.includes(event.key) && !inputProps.readOnly) {
			event.preventDefault();
			submit(highlighted >= 0 && suggestions[highlighted] ? suggestions[highlighted] : value);
		}
	};

	return (
		<React.Fragment>
			<TextInput
				{...inputProps}
				autoComplete='off'
				role='combobox'
				aria-autocomplete='list'
				aria-expanded={showMenu}
				aria-controls={menuId}
				aria-activedescendant={showMenu && highlighted >= 0 ? `${menuId}-${highlighted}` : undefined}
				value={value}
				onChange={(event) => {
					onChange(event.target.value);
					setOpen(true);
					setHighlighted(-1);
				}}
				onFocus={(event) => {
					setOpen(true);
					onFocus(event);
				}}
				onBlur={(event) => {
					setOpen(false);
					onBlur(event);
				}}
				onKeyDown={onKeyDown}
			/>
			{showMenu &&
			<ul id={menuId} role='listbox' className={menuClassName}>
				{suggestions.map((suggestion, index) =>
				<li
					key={suggestion}
					id={`${menuId}-${index}`}
					role='option'
					aria-selected={index === highlighted}
					className={index === highlighted ? 'is-highlighted' : ''}
					// mousedown, not click: a click comes after the box's blur, which would submit what was typed instead.
					onMouseDown={(event) => {
						event.preventDefault();
						submit(suggestion);
					}}
					onMouseEnter={() => setHighlighted(index)}
				>
					{suggestion}
				</li>
				)}
			</ul>
			}
		</React.Fragment>
	);
}
